import * as Sentry from "@sentry/react";

/**
 * Keep React alive when something outside it rewrites the DOM under it — in practice
 * Google Translate / Chrome's built-in "Translate this page" (Sentry BDAY-WEB-B / -C,
 * 2026-10-21: es-US Android on /home, the composer). Translation swaps our text nodes for
 * its own `<font>` wrappers; React still holds the originals, so its next commit calls
 * `insertBefore(node, <detached text>)` or `removeChild(<detached text>)`, the browser
 * throws NotFoundError, and the whole tree falls to the error boundary.
 *
 * The guard (the long-standing workaround from facebook/react#11538) handles exactly those
 * two impossible operations — a removal of an already-detached node is a no-op, an insert
 * before a detached reference appends instead — plus a breadcrumb, so the page keeps
 * working. The cost, only on a translated page: a translated fragment React meant to
 * remove can linger, and a newly shown element can land at the end of its parent. Not `translate="no"`:
 * that would take translation away from the members who rely on it.
 *
 * Only the mismatch path changes; every valid call goes straight through, so React's
 * normal behaviour is untouched.
 */
export function installTranslateDomGuard(): void {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      Sentry.addBreadcrumb({
        category: "dom-guard",
        level: "warning",
        message: "removeChild skipped: node is no longer a child (page translation?)",
      });
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      Sentry.addBreadcrumb({
        category: "dom-guard",
        level: "warning",
        message: "insertBefore appended: reference is no longer a child (page translation?)",
      });
      // Append rather than drop: content in a slightly wrong spot beats content missing
      // (verified against React 19 in Chromium with a simulated Translate rewrite).
      return originalInsertBefore.call(this, node, null) as T;
    }
    return originalInsertBefore.call(this, node, reference) as T;
  };
}
